import React, { useMemo } from 'react';
import { Alert, Typography, Button } from '@one-thd/sui-atomic-components';
import { useReturnStore } from '@src/stores/returnStore';
import { APICallState } from '@src/hooks/useMultiCallCreateReturn';
import { ItemToReturn } from '@src/models/returns';
import { RETURN_METHOD } from '@src/constants/general';
import { usePartialReturnRefund } from '@src/hooks/usePartialReturnRefund';
import { useNavigateToReturnSummary } from '@src/hooks/useNavigateToReturnSummary';
import ReturnReviewCard from '../shared/summary-cards/ReturnReviewCard';
import ItemImage from '../shared/items/ItemImage';
import ItemDescription from '../shared/items/ItemDescription';

interface ReturnItemRowProps {
  item: ItemToReturn;
}
interface ReturnCreateErrorStepProps {
  APICallStateProp: APICallState;
  onRetry: () => void;
}

const ReturnItemRow = ({ item }: ReturnItemRowProps) => (
  <div className="sui-flex sui-items-start sui-gap-6">
    <div className="sui-flex sui-w-full">
      <ItemImage
        imageUrl={item.itemImageURL}
        itemDescription={item.itemDescription}
      />
      <div className="sui-flex sui-flex-col">
        <ItemDescription
          brandName={item.brandName}
          itemDescription={item.itemDescription}
        />
      </div>
      <div className="sui-ml-auto sui-text-right">
        <Typography>Qty: {item?.returnQuantity}</Typography>
        <Typography weight="bold">{`$${item.itemCost?.toFixed(2)}`}</Typography>
      </div>
    </div>
  </div>
);

export const ReturnCreateErrorStep = ({
  APICallStateProp,
  onRetry,
}: ReturnCreateErrorStepProps) => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const navigateToReturnSummary = useNavigateToReturnSummary();

  // Separate successful and failed items based on completed API calls
  const { successfulItems, failedItems } = useMemo(() => {
    // Get all successful itemIds from completed calls
    const successfulItemIds = new Set<string>();

    APICallStateProp.completedCalls.forEach((call) => {
      if (call.success && call.data.shipments) {
        // Extract itemIds from all shipments in this successful call
        call.data.shipments.forEach((shipment) => {
          shipment.itemIds.forEach((itemId) => {
            successfulItemIds.add(itemId);
          });
        });
      }
    });

    // Split selectedItems into successful and failed based on itemIds
    // In-store items are always considered successful since they don't go through returnCreate
    const successful: ItemToReturn[] = [];
    const failed: ItemToReturn[] = [];

    selectedItems.forEach((item) => {
      const isInStoreItem = item.returnMethod === RETURN_METHOD.IN_STORE;
      const isInSuccessfulCall = successfulItemIds.has(item.lineItemId);

      if (isInStoreItem || isInSuccessfulCall) {
        successful.push(item);
      } else {
        failed.push(item);
      }
    });

    return { successfulItems: successful, failedItems: failed };
  }, [selectedItems, APICallStateProp.completedCalls]);

  // Use hook to handle refund calculation and button loading state
  const { showButtonLoader, handleContinueClick } = usePartialReturnRefund({
    successfulItems,
    totalSelectedItems: selectedItems.length,
    onFinishReturning: async () => {
      await navigateToReturnSummary(
        APICallStateProp.completedCalls,
        APICallStateProp.hasDuplicateError,
      );
    },
  });

  return (
    <>
      <ReturnReviewCard header="Items Not Processed">
        <div className="sui-mb-6">
          <Alert status="error" size="large">
            <Typography variant="body-base">
              We’re sorry. There was a temporary technical error processing the
              items below. Please try again or call 1-800-430-3376 for
              assistance.
            </Typography>
          </Alert>
        </div>
        <div className="sui-space-y-6">
          {failedItems.map((item, index) => (
            <ReturnItemRow key={`${item.lineItemId}-${index}`} item={item} />
          ))}
          <Button variant="secondary" fullWidth onClick={onRetry}>
            Try Again
          </Button>
        </div>
      </ReturnReviewCard>
      {successfulItems.length > 0 && (
        <ReturnReviewCard header="Items Ready to Return">
          <div className="sui-space-y-6">
            {successfulItems.map((item, index) => (
              <ReturnItemRow key={`${item.lineItemId}-${index}`} item={item} />
            ))}
            <Button
              fullWidth
              onClick={handleContinueClick}
              loading={showButtonLoader}
            >
              Finish Returning These Items
            </Button>
          </div>
        </ReturnReviewCard>
      )}
    </>
  );
};
